// ===============================================
// File: src/contexts/ParticipantContext.jsx
// Unified persistence (Folder OR Drive) via StorageContext.
// - Pieces: participants/pieces.json
// - Reads workshops/bookings (lookups)
// - Kiln sync (read-only) supports kiln/logs.json and kiln/kilnLogs.json
// - Rehydrates on boot and after restore (vault/files)
// ===============================================

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { useStorage } from "./StorageContext";

const ParticipantContext = createContext(null);
export const useParticipants = () => useContext(ParticipantContext);

// storage paths
const FILES = {
  pieces: "participants/pieces.json",
  kilnPrimary: "kiln/logs.json",
  kilnAlt: "kiln/kilnLogs.json",
  workshops: "workshops/workshops.json",
  bookings: "workshops/bookings.json",
};

const uid = () =>
  (globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2)) +
  "-" + Date.now().toString(36);

const nowISO = () => new Date().toISOString();

export function ParticipantProvider({ children }) {
  const {
    hasFolder, isDriveLinked, isDriveAuthed,
    loadJSON, saveJSON,
  } = useStorage();

  const hasWorkspace = !!hasFolder || (!!isDriveLinked && !!isDriveAuthed);

  // keep latest IO in refs (avoid effect churn)
  const loadRef = useRef(loadJSON);
  const saveRef = useRef(saveJSON);
  useEffect(() => { loadRef.current = loadJSON; }, [loadJSON]);
  useEffect(() => { saveRef.current = saveJSON; }, [saveJSON]);

  const [pieces, setPieces] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const kilnSnapshotRef = useRef(null);
  const kilnPollJitterMsRef = useRef(Math.floor(Math.random() * 20000));
  const loadedRef = useRef(false);
  const saveDebounce = useRef(0);

  /* ---------- hydrate pieces ---------- */
  const loadPieces = useRef(async () => {
    try {
      const disk = await loadRef.current(FILES.pieces, []);
      setPieces(Array.isArray(disk) ? disk : []);
    } catch {
      setPieces([]);
    }
  }).current;

  useEffect(() => {
    let alive = true;
    loadedRef.current = false;
    (async () => {
      if (!hasWorkspace) { if (alive) setPieces([]); loadedRef.current = true; return; }
      await loadPieces();
      loadedRef.current = true;
    })();
    return () => { alive = false; };
  }, [hasWorkspace, loadPieces]);

  // rehydrate after restore events
  useEffect(() => {
    const onRestored = async () => { if (hasWorkspace) await loadPieces(); };
    
    const onDisconnect = () => {
      // CRITICAL: Clear all data when workspace disconnected
      setPieces([]);
      loadedRef.current = false;
    };
    
    window.addEventListener("mudo:vault-restored", onRestored);
    window.addEventListener("mudo:files-restored", onRestored);
    window.addEventListener("mudo:workspace-disconnected", onDisconnect);
    return () => {
      window.removeEventListener("mudo:vault-restored", onRestored);
      window.removeEventListener("mudo:files-restored", onRestored);
      window.removeEventListener("mudo:workspace-disconnected", onDisconnect);
    };
  }, [hasWorkspace, loadPieces]);

  /* ---------- persist pieces (debounced) ---------- */
  useEffect(() => {
    if (!loadedRef.current || !hasWorkspace) return;
    clearTimeout(saveDebounce.current);
    saveDebounce.current = setTimeout(async () => {
      try { await saveRef.current(FILES.pieces, pieces); } catch {}
    }, 300);
    return () => clearTimeout(saveDebounce.current);
  }, [pieces, hasWorkspace]);

  /* ---------- workshop lookup (read-only) ---------- */
  const loadWorkshops = useRef(async () => {
    try {
      const list = await loadRef.current(FILES.workshops, []);
      setWorkshops(Array.isArray(list) ? list : []);
    } catch { setWorkshops([]); }
  }).current;

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!hasWorkspace) { if (alive) setWorkshops([]); return; }
      await loadWorkshops();
    })();
    return () => { alive = false; };
  }, [hasWorkspace, loadWorkshops]);

  useEffect(() => {
    const onRestored = async () => { if (hasWorkspace) await loadWorkshops(); };
    
    const onDisconnect = () => {
      // CRITICAL: Clear all data when workspace disconnected
      setWorkshops([]);
    };
    
    window.addEventListener("mudo:vault-restored", onRestored);
    window.addEventListener("mudo:files-restored", onRestored);
    window.addEventListener("mudo:workspace-disconnected", onDisconnect);
    return () => {
      window.removeEventListener("mudo:vault-restored", onRestored);
      window.removeEventListener("mudo:files-restored", onRestored);
      window.removeEventListener("mudo:workspace-disconnected", onDisconnect);
    };
  }, [hasWorkspace, loadWorkshops]);

  const workshopById = useMemo(
    () => new Map(workshops.map((w) => [w.id, w])),
    [workshops]
  );

  /* ---------- register pieces ---------- */
  const registerPieces = ({
    email,
    name,
    workshopId,
    bookingId,
    count = 1,
    defaultTitle = "Workshop Piece",
  }) => {
    const wk = workshopById.get(workshopId);
    const batchLabel = wk ? `WS-${wk.id}` : undefined;

    const created = Array.from({ length: Math.max(1, Number(count) || 1) }).map((_, i) => {
      const id = uid();
      return {
        id,
        email: (email || "").trim().toLowerCase(),
        name: (name || "").trim() || "Participant",
        workshopId,
        bookingId,
        title: `${defaultTitle}${count > 1 ? ` #${i + 1}` : ""}`,
        status: "created",
        batchLabel,
        history: [
          { at: nowISO(), from: null, to: "created", note: "Piece registered" },
        ],
      };
    });

    setPieces((p) => [...created, ...p]);
    return created;
  };

  /* ---------- update status ---------- */
  const setPieceStatus = (id, to, note) => {
    setPieces((list) =>
      list.map((p) =>
        p.id !== id
          ? p
          : {
              ...p,
              status: to,
              history: [...(p.history || []), { at: nowISO(), from: p.status, to, note }],
            }
      )
    );
  };

  /* ---------- auto-sync kiln logs (read-only, polling) ---------- */
  useEffect(() => {
    if (!hasWorkspace) return;

    const readKiln = async () => {
      try {
        const a = await loadRef.current(FILES.kilnPrimary, []);
        if (Array.isArray(a) && a.length) return a;
      } catch {}
      try {
        const b = await loadRef.current(FILES.kilnAlt, []);
        if (Array.isArray(b)) return b;
      } catch {}
      return null;
    };

    const syncFromKiln = async () => {
      const kilnLogs = await readKiln();
      if (!kilnLogs || kilnLogs.length === 0) return;

      const snap = JSON.stringify(kilnLogs);
      if (kilnSnapshotRef.current === snap) return;
      kilnSnapshotRef.current = snap;

      setPieces((list) => {
        let changed = false;
        const byLabel = new Map(kilnLogs.map((k) => [k.batchLabel, k]));
        const next = list.map((p) => {
          if (!p.batchLabel) return p;
          const k = byLabel.get(p.batchLabel);
          if (!k) return p;

          let target = null;
          const type = String(k.type || "").toLowerCase();
          if (k.status === "scheduled") target = "drying";
          if (k.status === "completed" && (type.includes("bisque"))) target = "bisque";
          if (k.status === "completed" && (type.includes("glaze"))) target = "glaze_fired";

          if (target && p.status !== target) {
            changed = true;
            return {
              ...p,
              status: target,
              history: [...(p.history || []), { at: nowISO(), from: p.status, to: target, note: `Kiln sync: ${k.type} ${k.status}` }],
            };
          }
          return p;
        });
        return changed ? next : list;
      });
    };

    const BASE_POLL_MS = 5000;
    const pollMs = BASE_POLL_MS + kilnPollJitterMsRef.current;
    const t = setInterval(syncFromKiln, pollMs);
    syncFromKiln(); // initial
    return () => clearInterval(t);
  }, [hasWorkspace]);

  // one-shot kiln snapshot on restore to avoid immediate re-run
  useEffect(() => {
    const once = async () => {
      if (!hasWorkspace) return;
      try {
        const a = await loadRef.current(FILES.kilnPrimary, []);
        const b = await loadRef.current(FILES.kilnAlt, []);
        const kilnLogs = Array.isArray(a) && a.length ? a : (Array.isArray(b) ? b : []);
        if (!kilnLogs.length) return;
        kilnSnapshotRef.current = JSON.stringify(kilnLogs);
      } catch {}
    };
    
    const onDisconnect = () => {
      // CRITICAL: Clear kiln snapshot when workspace disconnected
      kilnSnapshotRef.current = "[]";
    };
    
    window.addEventListener("mudo:vault-restored", once);
    window.addEventListener("mudo:files-restored", once);
    window.addEventListener("mudo:workspace-disconnected", onDisconnect);
    return () => {
      window.removeEventListener("mudo:vault-restored", once);
      window.removeEventListener("mudo:files-restored", once);
      window.removeEventListener("mudo:workspace-disconnected", onDisconnect);
    };
  }, [hasWorkspace]);

  /* ---------- search ---------- */
  const findPiecesByEmail = (email) => {
    const key = (email || "").trim().toLowerCase();
    return pieces.filter((p) => p.email === key);
  };

  const value = useMemo(
    () => ({ pieces, registerPieces, setPieceStatus, findPiecesByEmail }),
    [pieces]
  );

  return (
    <ParticipantContext.Provider value={value}>
      {children}
    </ParticipantContext.Provider>
  );
}
